import { QueryStringContext } from 'contexts/QueryStringContext';
import React, {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { getClientDataKPI } from 'utils/apiRequests';
import { formatSpendWithDecimalAndSymbol } from 'utils/number';
import { CheckPositiveValues } from './utils';
import { getCatgoriesForFilter } from 'utils/apiRequests';

const AnalyzeEditData = createContext();

const INITIAL_KPI_DATA = {
	isFetching: false,
	data: {
		total_spend_analysis: 0,
		total_spend_central: 0,
		total_vendor_count_analysis: 0,
		total_vendor_count_central: 0,
	},
};

export const AnalyzeEditDataProvider = ({
	children,
	currencySymbol,
	hideDecimals,
}) => {
	const { dateFrom, dateTo } = useContext(QueryStringContext);

	const dateRange = useMemo(
		() => ({
			dateFrom: dateFrom.split('T')[0],
			dateTo: dateTo.split('T')[0],
		}),
		[dateFrom, dateTo]
	);
	const allOptions = [
		{
			headerName: 'AP L1Category',
			field: 'aP_L1Category',
			colId: 'aP_L1Category',
			filter: 'agSetColumnFilter',
		},
		{
			headerName: 'AP L2Category',
			field: 'aP_L2Category',
			colId: 'aP_L2Category',
			filter: 'agSetColumnFilter',
		},
		{
			headerName: 'AP L3Category',
			field: 'aP_L3Category',
			colId: 'aP_L3Category',
			filter: 'agSetColumnFilter',
		},
		{
			headerName: 'AP L4Category',
			field: 'aP_L4Category',
			colId: 'aP_L4Category',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Vendor Name',
			field: 'vENDOR_NAME',
			filterParams: {
				filterOptions: [
					'contains',
					'notContains',
					'equals',
					'notEqual',
					'startsWith',
					'endsWith',
				],
			},
			colId: 'vENDOR_NAME',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Spend',
			field: 'sPEND',
			colId: 'sPEND',
			filter: 'agNumberColumnFilter',
			valueFormatter: params => {
				return formatSpendWithDecimalAndSymbol(
					params.value,
					currencySymbol,
					hideDecimals,
					2
				).toUpperCase();
			},
			sort: 'desc',
			sorted: true,
		},
		{
			headerName: 'Exist in AP D&I?',
			field: 'vENDOR_NAME_FLAG',
			filterParams: {
				filterOptions: [
					'contains',
					'notContains',
					'equals',
					'notEqual',
					'startsWith',
					'endsWith',
				],
			},
			colId: 'vENDOR_NAME_FLAG',
			filter: 'agTextColumnFilter',
			cellRenderer: params => {
				return `${params.value === '0' ? 'No' : 'Yes'}`;
			},
		},
		{
			headerName: 'State',
			field: 'sTATE_NAME',
			filterParams: {
				filterOptions: [
					'contains',
					'notContains',
					'equals',
					'notEqual',
					'startsWith',
					'endsWith',
				],
			},
			colId: 'sTATE_NAME',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'AP Certification',
			field: 'cERTIFICATION_NAME',
			filterParams: {
				filterOptions: [
					'contains',
					'notContains',
					'equals',
					'notEqual',
					'startsWith',
					'endsWith',
				],
			},
			colId: 'cERTIFICATION_NAME',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Description',
			field: 'dANDIDESCRIPTION',
			filterParams: {
				filterOptions: [
					'contains',
					'notContains',
					'equals',
					'notEqual',
					'startsWith',
					'endsWith',
				],
			},
			colId: 'dANDIDESCRIPTION',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Client Certification Name',
			field: 'cLIENT_CERTIFICATION_NAME',
			filterParams: {
				filterOptions: [
					'contains',
					'notContains',
					'equals',
					'notEqual',
					'startsWith',
					'endsWith',
				],
			},
			colId: 'cLIENT_CERTIFICATION_NAME',
			filter: 'agTextColumnFilter',
			width: 200,
			minWidth: 200,
			editable: true,
		},
	];

	const [columnDefs, setColumnDefs] = useState(allOptions);
	const [kpi, setKpi] = useState(INITIAL_KPI_DATA);

	const getCategories = async () => {
		const response = await getCatgoriesForFilter();
		if (response.succeeded) {
			if (response.data !== null) {
				if (Object.keys(response.data).length > 0) {
					const keys = Object.keys(response.data);
					const data = keys.map(x => {
						let word = x[0].toUpperCase() + x.slice(1);
						return {
							name: word,
							label: word,
							columnName: word,
							values: response.data[x].map(v => {
								return { label: v, value: v };
							}),
						};
					});
					const def = allOptions.map(col => {
						const filter = data.find(
							x => x.columnName.toUpperCase() === col.field.toUpperCase()
						);
						if (filter) {
							return {
								...col,
								filter: 'agSetColumnFilter',
								filterParams: {
									values: filter?.values.map(x => x.value),
								},
							};
						}
						return col;
					});
					setColumnDefs(def);
				}
			}
		}
	};

	useEffect(() => {
		getCategories();
	}, []);

	useEffect(
		() => {
			getKPI();
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[dateRange]
	);

	const updateColDefs = () => {
		// Restricting to update certification if Client Data is not uploaded and hiding the Is Exist column
		let filteredData = columnDefs.filter(
			x =>
				x.field !== 'vENDOR_NAME_FLAG' &&
				x.field !== 'cLIENT_CERTIFICATION_NAME'
		);
		setColumnDefs(filteredData);
	};

	const getKPI = async () => {
		setKpi(prevState => ({
			...prevState,
			isFetching: true,
		}));
		const response = await getClientDataKPI(dateRange);
		if (response.succeeded) {
			const result = CheckPositiveValues(response.data);
			setKpi(prevState => ({
				...prevState,
				data: result,
				isFetching: false,
			}));
		} else {
			setKpi(prevState => ({
				...prevState,
				isFetching: false,
			}));
		}
	};

	return (
		<AnalyzeEditData.Provider
			value={{
				columnDefs,
				kpi,
				updateColDefs,
			}}
		>
			{children}
		</AnalyzeEditData.Provider>
	);
};

export const useAnalyzeEditDataContext = () => {
	return useContext(AnalyzeEditData);
};
